import { createContext, ReactNode, useContext, useMemo, useReducer, useRef } from "react"
import { chatHistoryReducer, createInitialHistory, HistoryMessage } from "@/modules/chats/lib/chat-history-reducer"
import { AiFeatureKind } from "@/modules/chats/types/chat-message"

interface ChatHistoryValue {
  currentHistory: HistoryMessage[]
  canUndo: boolean
  canRedo: boolean
}

interface ChatHistoryActions {
  readCurrentHistory: () => HistoryMessage[]
  appendHistoryMessage: (content: string, addedAiFeature?: AiFeatureKind, contentBeforeAiFeature?: string) => void
  resetHistoryTo: (history: HistoryMessage[]) => void
  undo: VoidFunction
  redo: VoidFunction
  clearHistory: VoidFunction
}

const ChatHistoryValueContext = createContext<ChatHistoryValue | null>(null)
const ChatHistoryActionsContext = createContext<ChatHistoryActions | null>(null)

type ChatHistoryProviderProps = {
  children: ReactNode
  initialHistory?: HistoryMessage[]
}

export const ChatHistoryProvider = ({ children, initialHistory }: ChatHistoryProviderProps) => {
  const [{ past, present, future }, dispatch] = useReducer(chatHistoryReducer, undefined, () => ({
    past: [],
    present: initialHistory ?? createInitialHistory(),
    future: [],
  }))

  const canUndo = past.length > 0
  const canRedo = future.length > 0

  const presentRef = useRef(present)
  presentRef.current = present

  const actions = useMemo<ChatHistoryActions>(
    () => ({
      readCurrentHistory: () => presentRef.current,
      appendHistoryMessage: (content, addedAiFeature, contentBeforeAiFeature) =>
        dispatch({ type: "append", content, addedAiFeature, contentBeforeAiFeature }),
      resetHistoryTo: (history) => dispatch({ type: "reset", present: history }),
      undo: () => dispatch({ type: "undo" }),
      redo: () => dispatch({ type: "redo" }),
      clearHistory: () => dispatch({ type: "reset" }),
    }),
    [],
  )

  const value = useMemo<ChatHistoryValue>(
    () => ({ currentHistory: present, canUndo, canRedo }),
    [present, canUndo, canRedo],
  )

  return (
    <ChatHistoryActionsContext.Provider value={actions}>
      <ChatHistoryValueContext.Provider value={value}>{children}</ChatHistoryValueContext.Provider>
    </ChatHistoryActionsContext.Provider>
  )
}

const useChatHistoryValue = (): ChatHistoryValue => {
  const value = useContext(ChatHistoryValueContext)
  if (!value) throw new Error("Missing ChatHistoryProvider in the tree")

  return value
}

const useChatHistoryActions = (): ChatHistoryActions => {
  const actions = useContext(ChatHistoryActionsContext)
  if (!actions) throw new Error("Missing ChatHistoryProvider in the tree")

  return actions
}

export const useChatHistoryMessages = (): HistoryMessage[] => useChatHistoryValue().currentHistory

export const useChatHistoryUndoRedo = (): {
  canUndo: boolean
  canRedo: boolean
  undo: VoidFunction
  redo: VoidFunction
} => {
  const { canUndo, canRedo } = useChatHistoryValue()
  const { undo, redo } = useChatHistoryActions()

  return { canUndo, canRedo, undo, redo }
}

export const useAppendChatHistoryMessage = (): ChatHistoryActions["appendHistoryMessage"] =>
  useChatHistoryActions().appendHistoryMessage

export const useResetChatHistoryTo = (): ChatHistoryActions["resetHistoryTo"] => useChatHistoryActions().resetHistoryTo

export const useClearChatHistory = (): ChatHistoryActions["clearHistory"] => useChatHistoryActions().clearHistory

export const useReadChatHistory = (): ChatHistoryActions["readCurrentHistory"] =>
  useChatHistoryActions().readCurrentHistory
